import { useState, useEffect, useCallback } from "react";
import {
  Mail,
  Phone,
  Trash2,
  Eye,
  CheckCircle,
  Circle,
  Search,
  RefreshCw,
  X,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import {
  getAdminInquiries,
  updateAdminInquiry,
  deleteAdminInquiry,
} from "../../api/contactApi";
import { useToast } from "../../context/ToastContext";
import { formatDate } from "../../utils/format";
import "./AdminInquiries.css";

export default function AdminInquiries() {
  const { addToast } = useToast();

  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [filter, setFilter] = useState("all"); // 'all' | 'unread' | 'read'
  const [search, setSearch] = useState("");
  const [activeInquiry, setActiveInquiry] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const isReadParam =
        filter === "unread" ? false : filter === "read" ? true : undefined;
      const data = await getAdminInquiries({
        page,
        pageSize,
        isRead: isReadParam,
        search,
      });
      setInquiries(data.items || []);
      setTotalCount(data.totalCount || 0);
    } catch {
      addToast("Failed to load customer inquiries.", "error");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter, search, addToast]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleOpenDetail = (inquiry) => {
    setActiveInquiry(inquiry);
    setAdminNote(inquiry.adminNotes || "");
    if (!inquiry.isRead) {
      handleToggleRead(inquiry, true);
    }
  };

  const handleToggleRead = async (inquiry, newStatus) => {
    try {
      await updateAdminInquiry(inquiry.id, { isRead: newStatus });
      setInquiries((prev) =>
        prev.map((i) => (i.id === inquiry.id ? { ...i, isRead: newStatus } : i))
      );
      if (activeInquiry && activeInquiry.id === inquiry.id) {
        setActiveInquiry((prev) => ({ ...prev, isRead: newStatus }));
      }
    } catch {
      addToast("Failed to update status.", "error");
    }
  };

  const handleSaveNote = async () => {
    if (!activeInquiry) return;
    setSavingNote(true);
    try {
      await updateAdminInquiry(activeInquiry.id, { adminNotes: adminNote });
      setInquiries((prev) =>
        prev.map((i) =>
          i.id === activeInquiry.id ? { ...i, adminNotes: adminNote } : i
        )
      );
      setActiveInquiry((prev) => ({ ...prev, adminNotes: adminNote }));
      addToast("Admin notes saved.", "success");
    } catch {
      addToast("Failed to save note.", "error");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer inquiry?")) {
      return;
    }
    try {
      await deleteAdminInquiry(id);
      addToast("Inquiry deleted.", "success");
      if (activeInquiry && activeInquiry.id === id) {
        setActiveInquiry(null);
      }
      fetchInquiries();
    } catch {
      addToast("Failed to delete inquiry.", "error");
    }
  };

  const unreadCount = inquiries.filter((i) => !i.isRead).length;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <div className="admin-inquiries-page">
      {/* ── Header ── */}
      <div className="admin-inquiries-header">
        <div>
          <h1>Customer Inquiries</h1>
          <p>Review and respond to messages submitted through the contact page</p>
        </div>
        <div className="admin-inquiries-stats">
          <div className="admin-inquiry-stat">
            <span className="admin-inquiry-stat__label">Total</span>
            <span className="admin-inquiry-stat__val">{totalCount}</span>
          </div>
          <div className="admin-inquiry-stat">
            <span className="admin-inquiry-stat__label">Unread (Page)</span>
            <span className="admin-inquiry-stat__val admin-inquiry-stat__val--unread">
              {unreadCount}
            </span>
          </div>
        </div>
      </div>

      {/* ── Toolbar & Filters ── */}
      <div className="admin-inquiries-toolbar">
        <div className="admin-inquiries-filter-group">
          <button
            type="button"
            className={`admin-filter-pill ${
              filter === "all" ? "admin-filter-pill--active" : ""
            }`}
            onClick={() => {
              setFilter("all");
              setPage(1);
            }}
          >
            All Messages
          </button>
          <button
            type="button"
            className={`admin-filter-pill ${
              filter === "unread" ? "admin-filter-pill--active" : ""
            }`}
            onClick={() => {
              setFilter("unread");
              setPage(1);
            }}
          >
            Unread
          </button>
          <button
            type="button"
            className={`admin-filter-pill ${
              filter === "read" ? "admin-filter-pill--active" : ""
            }`}
            onClick={() => {
              setFilter("read");
              setPage(1);
            }}
          >
            Read
          </button>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div className="admin-inquiries-search-wrap">
            <Search size={15} className="admin-inquiries-search-icon" />
            <input
              type="text"
              className="admin-inquiries-search-input"
              placeholder="Search by name, email, or text..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <button
            type="button"
            className="admin-icon-btn"
            title="Refresh"
            onClick={fetchInquiries}
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="admin-inquiries-table-wrap">
        {loading ? (
          <div className="admin-empty-state">
            <p>Loading customer inquiries...</p>
          </div>
        ) : inquiries.length === 0 ? (
          <div className="admin-empty-state">
            <MessageSquare size={36} style={{ marginBottom: 12, opacity: 0.5 }} />
            <p>No customer inquiries found.</p>
          </div>
        ) : (
          <table className="admin-inquiries-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Customer</th>
                <th>Subject</th>
                <th>Message Preview</th>
                <th>Received</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((item) => (
                <tr
                  key={item.id}
                  className={!item.isRead ? "admin-inquiry-row--unread" : ""}
                >
                  <td>
                    <span
                      className={`admin-inquiry-badge ${
                        item.isRead
                          ? "admin-inquiry-badge--read"
                          : "admin-inquiry-badge--unread"
                      }`}
                    >
                      {item.isRead ? (
                        <>
                          <CheckCircle size={12} />
                          <span>Read</span>
                        </>
                      ) : (
                        <>
                          <Circle size={10} fill="currentColor" />
                          <span>New</span>
                        </>
                      )}
                    </span>
                  </td>
                  <td>
                    <div className="admin-customer-info">
                      <span className="admin-customer-name">{item.name}</span>
                      <span className="admin-customer-email">{item.email}</span>
                      {item.phoneNumber && (
                        <span className="admin-customer-phone">
                          {item.phoneNumber}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="admin-subject-tag">{item.subject}</span>
                  </td>
                  <td>
                    <div className="admin-inquiry-preview" title={item.message}>
                      {item.message}
                    </div>
                  </td>
                  <td style={{ fontSize: "12.5px", color: "var(--text-muted)" }}>
                    {formatDate(item.createdAtUtc)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div
                      className="admin-inquiry-actions"
                      style={{ justifyContent: "flex-end" }}
                    >
                      <button
                        type="button"
                        className="admin-icon-btn"
                        title="View details"
                        onClick={() => handleOpenDetail(item)}
                      >
                        <Eye size={15} />
                      </button>
                      <button
                        type="button"
                        className="admin-icon-btn admin-icon-btn--danger"
                        title="Delete inquiry"
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 20,
          }}
        >
          <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>
            Showing page {page} of {totalPages} ({totalCount} total)
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              className="admin-icon-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              className="admin-icon-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Inquiry Detail Modal ── */}
      {activeInquiry && (
        <div className="admin-modal-overlay" onClick={() => setActiveInquiry(null)}>
          <div
            className="admin-modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="admin-modal-header">
              <h2>Customer Inquiry #{activeInquiry.id}</h2>
              <button
                type="button"
                className="admin-modal-close"
                onClick={() => setActiveInquiry(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="admin-modal-body">
              {/* Customer summary */}
              <div className="admin-detail-grid">
                <div className="admin-detail-item">
                  <span className="admin-detail-label">Name</span>
                  <span className="admin-detail-val">{activeInquiry.name}</span>
                </div>
                <div className="admin-detail-item">
                  <span className="admin-detail-label">Email</span>
                  <span className="admin-detail-val">{activeInquiry.email}</span>
                </div>
                <div className="admin-detail-item">
                  <span className="admin-detail-label">Phone</span>
                  <span className="admin-detail-val">
                    {activeInquiry.phoneNumber || "Not provided"}
                  </span>
                </div>
                <div className="admin-detail-item">
                  <span className="admin-detail-label">Subject Category</span>
                  <span className="admin-detail-val">{activeInquiry.subject}</span>
                </div>
                <div className="admin-detail-item">
                  <span className="admin-detail-label">Date Submitted</span>
                  <span className="admin-detail-val">
                    {formatDate(activeInquiry.createdAtUtc)}
                  </span>
                </div>
                <div className="admin-detail-item">
                  <span className="admin-detail-label">IP Address</span>
                  <span className="admin-detail-val">
                    {activeInquiry.ipAddress || "Unknown"}
                  </span>
                </div>
              </div>

              {/* Full Message */}
              <div className="admin-msg-box">
                <h4>Message Content:</h4>
                <p>{activeInquiry.message}</p>
              </div>

              {/* Admin Notes */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "var(--text-muted)",
                    marginBottom: 6,
                  }}
                >
                  Internal Admin Notes
                </label>
                <textarea
                  rows={3}
                  className="admin-notes-area"
                  placeholder="Add private notes on resolution, followup date, etc..."
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                />
                <button
                  type="button"
                  className="btn-outline"
                  style={{ marginTop: 8, fontSize: "12px", padding: "6px 14px" }}
                  disabled={savingNote}
                  onClick={handleSaveNote}
                >
                  {savingNote ? "Saving..." : "Save Note"}
                </button>
              </div>
            </div>

            <div className="admin-modal-footer">
              <div style={{ display: "flex", gap: "10px" }}>
                <a
                  href={`mailto:${activeInquiry.email}?subject=Re:%20Terry%20Land%20Inquiry%20-%20${encodeURIComponent(
                    activeInquiry.subject
                  )}`}
                  className="btn-primary"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: "13px",
                    padding: "8px 16px",
                    textDecoration: "none",
                  }}
                >
                  <Mail size={14} />
                  <span>Reply via Email</span>
                  <ExternalLink size={12} />
                </a>

                {activeInquiry.phoneNumber && (
                  <a
                    href={`https://wa.me/${activeInquiry.phoneNumber.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: "13px",
                      padding: "8px 16px",
                      textDecoration: "none",
                    }}
                  >
                    <Phone size={14} />
                    <span>WhatsApp</span>
                  </a>
                )}
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  className="btn-outline"
                  style={{ fontSize: "13px", padding: "8px 14px" }}
                  onClick={() =>
                    handleToggleRead(activeInquiry, !activeInquiry.isRead)
                  }
                >
                  {activeInquiry.isRead ? "Mark as Unread" : "Mark as Read"}
                </button>
                <button
                  type="button"
                  className="btn-outline"
                  style={{
                    color: "#ef4444",
                    borderColor: "rgba(239, 68, 68, 0.4)",
                    fontSize: "13px",
                    padding: "8px 14px",
                  }}
                  onClick={() => handleDelete(activeInquiry.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
